/**
 * DecoWordmark — canonical DECO brand mark.
 *
 * Brand spec (see memory: feedback_deco_logo_styling.md):
 *   - font-weight: 200
 *   - font-style: italic
 *   - font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif
 *   - letter-spacing: 16px in headers, 24px on the hero/auth gate
 *
 * Why this exists:
 *   Prior to consolidation the wordmark was inlined in 18+ places with
 *   varying letter-spacing (8px / 12px / 16px / 24px). 8px and 12px are
 *   OFF-SPEC and were the source of the drift. This component locks the
 *   weight/style/family/spacing to brand, and exposes only `size` + `color`
 *   so callers can adapt to context without re-deciding typography.
 *
 * Sizes:
 *   - xs  → 13px / spacing 5px    ("Powered by DECO" footnotes / inline credits)
 *   - sm  → 22px / spacing 16px   (compact headers, e.g. CreateOrganization)
 *   - md  → 36px / spacing 16px   (standard auth/setup modal header)
 *   - lg  → 56px / spacing 16px   (page-anchored marks)
 *   - hero → 72px / spacing 24px  (BetaAccessGate, landing)
 *
 * Navbar deliberately uses smaller letter-spacing (12px desktop / 8px
 * mobile) because the wordmark is competing for horizontal space with
 * nav links — it's an intentional adaptation, not drift. To use the
 * adapted variant pass size="navbar".
 *
 * Accessibility: rendered as an inline-block <span> with aria-label so
 * screen readers announce "DECO Energy" rather than spelling "D E C O".
 */
import React from 'react';

// Exported so the BETA line beneath the mark (DEC-1184) can derive its
// centring offset from the wordmark's real tracking + padding instead of
// hardcoding pixels that would silently desync if the spec changed.
export const SIZE_PRESETS = {
  xs:     { fontSize: '13px', letterSpacing: '5px', paddingRight: '5px' },
  sm:     { fontSize: '22px', letterSpacing: '16px', paddingRight: '16px' },
  md:     { fontSize: '36px', letterSpacing: '16px', paddingRight: '16px' },
  lg:     { fontSize: '56px', letterSpacing: '16px', paddingRight: '16px' },
  hero:   { fontSize: '72px', letterSpacing: '24px', paddingRight: '24px' },
  // Navbar variants — letter-spacing is intentionally tighter to coexist
  // with adjacent nav links. The fontSize values match the existing
  // Navbar.js inline treatment exactly so swapping in DecoWordmark is
  // a visual no-op.
  navbar:        { fontSize: '36px', letterSpacing: '12px', paddingRight: '12px' },
  'navbar-mobile': { fontSize: '28px', letterSpacing: '8px', paddingRight: '8px' },
};

const DecoWordmark = ({
  size = 'md',
  color = '#1a1a1a',
  className,
  style,
  as: As = 'span',
  // `block` lets callers easily render a centered hero mark without
  // having to wrap it. Defaults to inline to preserve current call-site
  // assumptions.
  block = false,
}) => {
  const preset = SIZE_PRESETS[size] || SIZE_PRESETS.md;

  return (
    <As
      className={className}
      aria-label="DECO Energy"
      style={{
        // Locked brand typography — do not override per-call.
        fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif",
        fontWeight: 200,
        fontStyle: 'italic',
        // font-stretch INHERITS, and the 2026-08-09 condensed-display rules
        // set `font-stretch: condensed` on every h1–h6 / th / button. A
        // wordmark nested in any of those (the landing hero <h1>, a button)
        // inherited it, and on Apple platforms the only Helvetica Neue
        // condensed face is the BOLD one — so the mark rendered heavy and
        // squeezed instead of thin. Locking it here keeps the brand mark
        // immune wherever it is nested.
        fontStretch: 'normal',
        color,
        userSelect: 'none',
        display: block ? 'block' : 'inline-block',
        // Size-driven values — derived from the preset, can still be
        // overridden via `style` when a one-off truly needs it.
        ...preset,
        ...style,
      }}
    >
      DECO
    </As>
  );
};

export default DecoWordmark;
